<template>
  <div class="public-shop">
    <header class="shop-header" :style="{ background: themeColor }">
      <div class="container py-3">
        <router-link :to="shopPath('')" class="text-white" style="text-decoration:none;">
          <i class="fas fa-arrow-left"></i> Kembali ke Toko
        </router-link>
      </div>
    </header>

    <div class="container mt-4" v-if="loading">
      <div class="text-center p-5"><div class="spinner-border"></div></div>
    </div>

    <div class="container mt-4" v-else-if="!settings">
      <div class="text-center p-5">
        <h3>Donasi tidak tersedia</h3>
        <p class="text-muted">Toko ini belum mengaktifkan fitur donasi.</p>
      </div>
    </div>

    <div class="container mt-4" v-else>
      <!-- Campaign header -->
      <div class="card mb-4 overflow-hidden">
        <img v-if="settings.cover_image_url" :src="settings.cover_image_url" class="w-100" style="max-height:280px; object-fit:cover;">
        <div class="card-body p-4">
          <h2>{{ settings.title }}</h2>
          <p class="text-muted" v-if="settings.description">{{ settings.description }}</p>

          <div v-if="settings.show_progress && settings.target_amount > 0" class="mt-3">
            <div class="d-flex justify-content-between mb-1">
              <span>Terkumpul: <b>{{ formatRupiah(settings.collected_amount) }}</b></span>
              <span>Target: {{ formatRupiah(settings.target_amount) }}</span>
            </div>
            <div class="progress" style="height:12px;">
              <div class="progress-bar bg-success" :style="{ width: progressPct + '%' }"></div>
            </div>
            <small class="text-muted">{{ progressPct }}% dari target</small>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-7">
          <div class="card p-4 mb-3">
            <h5>Data Donatur</h5>
            <div class="row">
              <div class="col-md-12 mb-2" v-if="!form.is_anonymous">
                <label>Nama (opsional)</label>
                <input v-model="form.donor_name" class="form-control" placeholder="Nama Anda">
              </div>
              <div class="col-md-6 mb-2">
                <label>Nomor WhatsApp *</label>
                <input v-model="form.donor_phone" class="form-control" placeholder="08xxxx" required>
              </div>
              <div class="col-md-6 mb-2">
                <label>Email *</label>
                <input v-model="form.donor_email" type="email" class="form-control" required>
              </div>
            </div>
            <div v-if="settings.allow_anonymous" class="form-check mt-2">
              <input id="anon" type="checkbox" class="form-check-input" v-model="form.is_anonymous">
              <label for="anon" class="form-check-label">Donasi sebagai anonim</label>
            </div>
          </div>

          <div class="card p-4 mb-3">
            <h5>Nominal Donasi</h5>
            <div class="d-flex flex-wrap mb-3" style="gap:8px;">
              <button v-for="amt in settings.suggested_amounts" :key="amt"
                class="btn" :class="form.amount === amt ? 'btn-primary' : 'btn-outline-primary'"
                @click="selectAmount(amt)">
                {{ formatRupiah(amt) }}
              </button>
              <button class="btn" :class="customAmount ? 'btn-primary' : 'btn-outline-secondary'" @click="customAmount = true">
                Nominal Lain
              </button>
            </div>
            <div v-if="customAmount || !settings.suggested_amounts.includes(form.amount)">
              <label>Nominal Custom (min Rp 1.000)</label>
              <input v-model.number="form.amount" type="number" min="1000" step="1000" class="form-control">
            </div>
          </div>

          <div class="card p-4 mb-3">
            <h5>Pesan / Doa (Opsional)</h5>
            <textarea v-model="form.message" class="form-control" rows="2" placeholder="Semoga bermanfaat..."></textarea>
          </div>

          <div class="card p-4 mb-3 shop-card">
            <h5>Metode Pembayaran</h5>
            <PaymentChannelPicker
              :channels="paymentChannels"
              :loading="loadingChannels"
              v-model="form.payment_method"
            />
          </div>
        </div>

        <div class="col-md-5">
          <div class="card p-4 mb-3" style="position:sticky; top:20px;">
            <h5>Ringkasan Donasi</h5>
            <div class="d-flex justify-content-between mb-1">
              <span>Nominal donasi:</span>
              <span>{{ formatRupiah(form.amount) }}</span>
            </div>
            <div v-if="isTripayMethod && selectedChannel" class="d-flex justify-content-between mb-1">
              <span>Biaya gateway ({{ selectedChannel.name }}):</span>
              <span>{{ formatRupiah(gatewayFee) }}</span>
            </div>
            <div v-if="isTripayMethod && selectedChannel" class="d-flex justify-content-between mb-1 small text-muted">
              <span>Potongan platform:</span>
              <span>{{ formatRupiah(starsenderFee) }}</span>
            </div>
            <hr v-if="isTripayMethod && selectedChannel">
            <div class="d-flex justify-content-between mb-3">
              <b>{{ isTripayMethod && selectedChannel ? 'Total bayar:' : 'Total:' }}</b>
              <b class="text-primary">{{ formatRupiah(totalPay) }}</b>
            </div>
            <small v-if="isTripayMethod && selectedChannel" class="text-muted d-block mb-3">
              Potongan platform dibebankan ke penerima donasi, bukan ditambahkan ke total bayar Anda.
            </small>
            <button class="btn btn-success btn-lg btn-block" @click="submit" :disabled="submitting || !canSubmit">
              <span v-if="submitting" class="spinner-border spinner-border-sm mr-2"></span>
              💚 Donasi Sekarang
            </button>
          </div>

          <!-- Donor wall -->
          <div v-if="settings.show_donor_wall && wall.length" class="card p-4 mb-3">
            <h6>💚 Donatur Terbaru</h6>
            <div v-for="(w, i) in wall" :key="i" class="border-bottom py-2">
              <div class="d-flex justify-content-between">
                <b>{{ w.donor_display }}</b>
                <span class="text-success">{{ formatRupiah(w.amount) }}</span>
              </div>
              <small v-if="w.message" class="text-muted">{{ w.message }}</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import publicShopPaths from './public_shop_paths';
import PaymentChannelPicker from '@/components/shop_public/PaymentChannelPicker.vue';
import { applyShopTheme, shopHeaderBackground, pickShopTheme, shopPublicMixin } from '@/components/shop_public/shop_public_utils';
import '@/components/shop_public/shop_public.css';

export default {
  mixins: [publicShopPaths, shopPublicMixin],
  components: { PaymentChannelPicker },
  data() {
    return {
      loading: true,
      submitting: false,
      settings: null,
      wall: [],
      customAmount: false,
      paymentChannels: [],
      loadingChannels: false,
      form: {
        donor_name: '',
        donor_phone: '',
        donor_email: '',
        amount: 10000,
        message: '',
        is_anonymous: false,
        payment_method: '',
      },
    };
  },
  computed: {
    themeColor() {
      return shopHeaderBackground(pickShopTheme(this.settings, this.$store.state.public_shop.shop));
    },
    progressPct() {
      if (!this.settings?.target_amount) return 0;
      return Math.min(100, Math.round((this.settings.collected_amount / this.settings.target_amount) * 100));
    },
    canSubmit() {
      return this.form.donor_phone && this.form.donor_email && this.form.amount >= 1000 && this.form.payment_method;
    },
    selectedChannel() {
      return this.paymentChannels.find(ch => ch.code === this.form.payment_method) || null;
    },
    isTripayMethod() {
      const m = this.form.payment_method;
      return m && m !== 'transfer_manual' && m !== 'cod';
    },
    gatewayFee() {
      if (!this.isTripayMethod || !this.selectedChannel) return 0;
      return this.calcGatewayFee(this.selectedChannel, this.form.amount);
    },
    starsenderFee() {
      return 5000;
    },
    totalPay() {
      return this.form.amount + (this.isTripayMethod ? this.gatewayFee : 0);
    },
  },
  async mounted() {
    if (!this.effectiveSlug && this.onCustomDomain) {
      await this.$store.dispatch('public_shop/public_get_shop_by_domain');
    }
    await this.$store.dispatch('public_shop/public_get_shop', this.effectiveSlug);
    await Promise.all([this.loadSettings(), this.loadWall(), this.loadPaymentChannels()]);
    this.loading = false;
  },
  methods: {
    async loadSettings() {
      try {
        const r = await this.$store.dispatch('public_shop/public_get_donation_settings', this.effectiveSlug);
        this.settings = r.data.data;
        applyShopTheme(pickShopTheme(this.settings, this.$store.state.public_shop.shop));
        if (this.settings.suggested_amounts?.length) {
          this.form.amount = this.settings.suggested_amounts[0];
        }
      } catch (e) {
        this.settings = null;
      }
    },
    async loadWall() {
      try {
        const r = await this.$store.dispatch('public_shop/public_get_donation_wall', this.effectiveSlug);
        this.wall = r.data.data.wall || [];
      } catch (e) { this.wall = []; }
    },
    async loadPaymentChannels() {
      this.loadingChannels = true;
      try {
        const axios = require('axios');
        const base = (axios.defaults.baseURL || '').replace(/\/api\/v1\/?$/, '');
        const r = await axios.get(base + '/api/public/shop/' + this.effectiveSlug + '/payment-channels', { headers: { Authorization: '' } });
        this.paymentChannels = r.data.data.channels || [];
      } catch (e) { /* noop */ }
      this.loadingChannels = false;
    },
    selectAmount(amt) {
      this.form.amount = amt;
      this.customAmount = false;
    },
    calcGatewayFee(ch, amount) {
      let fee = ch.fee_flat || 0;
      if (ch.fee_percent) fee += Math.round(amount * ch.fee_percent / 100);
      return fee;
    },
    async submit() {
      this.submitting = true;
      try {
        const payload = {
          donor_name: this.form.is_anonymous ? '' : this.form.donor_name,
          donor_phone: this.form.donor_phone,
          donor_email: this.form.donor_email,
          amount: this.form.amount,
          message: this.form.message,
          is_anonymous: this.form.is_anonymous,
          payment_method: this.form.payment_method,
        };
        const r = await this.$store.dispatch('public_shop/public_create_donation', { slug: this.effectiveSlug, payload });
        const donation = r.data.data.donation;
        this.$router.push(this.shopPath('/donasi/' + donation.donation_number));
      } catch (e) {
        this.$toast.error(e.response?.data?.message || 'Gagal membuat donasi');
      }
      this.submitting = false;
    },
  },
};
</script>
